import React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import { useUpdateUserProfileMutation } from "../dashboard/dashboardApiSlice";
import { clearEmailUpdatePrompt } from "./authSlice";
import toast from "../../../helpers/toast";

function ParentEmailPromptDialog({ open }) {
  const dispatch = useDispatch();
  const [updateUserProfile, { isLoading }] = useUpdateUserProfileMutation();

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: yup.object({
      email: yup
        .string()
        .email("Please enter a valid email address")
        .required("Email is required"),
    }),
    onSubmit: async (values) => {
      try {
        await updateUserProfile({ email: values.email }).unwrap();
        toast.success("Email saved successfully");
        dispatch(clearEmailUpdatePrompt());
      } catch (err) {
        toast.error(err?.message || "Failed to save email");
      }
    },
  });

  const handleSkip = () => {
    dispatch(clearEmailUpdatePrompt());
  };

  return (
    <Dialog open={open} disableEscapeKeyDown>
      <DialogTitle>Add Your Email Address</DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Your account was created with a phone number. Adding an email allows
            you to receive notifications and recover your account if needed.
          </Typography>
        </Box>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ "& > *": { mb: 2 } }}>
            <FormControl error={formik.touched.email && Boolean(formik.errors.email)} fullWidth>
              <Typography variant="formSubHeading">Email address</Typography>
              <StyledTextField
                autoFocus
                id="email"
                name="email"
                type="email"
                placeholder="e.g. parent@example.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
              />
              {formik.touched.email && formik.errors.email && (
                <FormHelperText>{formik.errors.email}</FormHelperText>
              )}
            </FormControl>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button variant="text" onClick={handleSkip} disabled={isLoading}>
                Skip for now
              </Button>
              <StyledLoadingButton
                type="submit"
                variant="contained"
                loading={isLoading}
              >
                Save Email
              </StyledLoadingButton>
            </Box>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ParentEmailPromptDialog;
